import type { initHoverActions } from 'shared/position/hover-actions.ts'

import { useEffect, useMemo, useRef } from 'react'

interface UsePopoverPositionProps {
  anchor?: HTMLElement | null
  hover?: ReturnType<typeof initHoverActions>
  onClose: () => void
}

interface UsePopoverPositionReturn {
  popoverListeners: Record<string, any>
  ref: React.RefObject<HTMLDivElement>
  style: React.CSSProperties
}

export function usePopoverPosition({
  anchor,
  hover,
  onClose
}: UsePopoverPositionProps): UsePopoverPositionReturn {
  const fallbackRef = useRef<HTMLDivElement | null>(null)
  const ref = hover ? hover.useSyncPopoverState() : fallbackRef

  const style: React.CSSProperties = useMemo(() => {
    const effectiveAnchor = anchor ?? (document.querySelector('.gif-anchor.active-info') as HTMLElement | null)
    if (!effectiveAnchor) return { display: 'none' }
    const rect = effectiveAnchor.getBoundingClientRect()
    return {
      left: rect.left,
      top: rect.top - 8,
      transform: 'translateY(-100%)'
    }
  }, [anchor])

  useEffect(() => {
    if (hover) return

    function handle(event: MouseEvent) {
      const target = event.target as Node
      const currentAnchor = anchor ?? (document.querySelector('.gif-anchor.active-info') as HTMLElement | null)
      if (ref.current && !ref.current.contains(target) && currentAnchor && !currentAnchor.contains(target)) {
        onClose()
      }
    }

    window.addEventListener('mousedown', handle)
    return () => window.removeEventListener('mousedown', handle)
  }, [onClose, anchor, hover, ref])

  return {
    popoverListeners: hover ? hover.popoverListeners : {},
    ref,
    style
  }
}
